import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFavorites } from "@/lib/favorites";
import { useLang } from "@/lib/i18n";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";

export default function Favorites() {
  const { user } = useAuth();
  const { refresh } = useFavorites();
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/favorites");
        setItems(data);
      } finally { setLoading(false); }
    })();
  }, [user, refresh]);

  if (!user) {
    return (
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 text-center" data-testid="favorites-guest">
        <Heart className="w-16 h-16 text-[#C85A32] mx-auto mb-6" />
        <h1 className="heading font-extrabold text-3xl mb-3">{t("fav.guest.title")}</h1>
        <p className="text-[#4B5563] mb-6">{t("fav.guest.subtitle")}</p>
        <Link to="/connexion"><Button className="pill-btn bg-[#1A3322] rounded-full" data-testid="favorites-login-btn">{t("header.login")}</Button></Link>
      </div>
    );
  }

  const products = items.filter((i) => i.kind === "product");
  const services = items.filter((i) => i.kind === "service");

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12" data-testid="favorites-page">
      <div className="flex items-center gap-3 mb-8">
        <Heart className="w-8 h-8 text-[#C85A32] fill-[#C85A32]" />
        <h1 className="heading font-extrabold text-3xl sm:text-4xl">{t("fav.title")}</h1>
      </div>

      {loading ? (
        <p className="text-[#4B5563]">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <div className="text-center py-20" data-testid="favorites-empty">
          <p className="text-[#4B5563] mb-4">{t("fav.empty")}</p>
          <p className="text-sm text-[#4B5563] mb-6">{t("fav.empty.hint")}</p>
          <Link to="/produits"><Button className="pill-btn bg-[#1A3322] rounded-full" data-testid="favorites-explore">{t("fav.explore")}</Button></Link>
        </div>
      ) : (
        <div className="space-y-12">
          {products.length > 0 && (
            <section data-testid="favorites-products">
              <h2 className="heading font-bold text-2xl mb-6 text-[#1A3322]">{t("fav.section.products")} ({products.length})</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {products.map((f) => <ProductCard key={f.id} p={f.item} kind="product" />)}
              </div>
            </section>
          )}
          {services.length > 0 && (
            <section data-testid="favorites-services">
              <h2 className="heading font-bold text-2xl mb-6 text-[#1A3322]">{t("fav.section.services")} ({services.length})</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {services.map((f) => <ProductCard key={f.id} p={f.item} kind="service" />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
